import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { 
  Users, Building2, MapPin, Trophy, Handshake, TrendingUp,
  CheckCircle, ArrowRight, Star, Globe, Shield, Zap
} from 'lucide-react';

const PartnerPage = () => {
  const [selectedPartnerType, setSelectedPartnerType] = useState(null);

  const partnerTypes = [
    {
      id: 'crew',
      icon: Users,
      title: 'Professional Sports Crew',
      subtitle: 'Coaches, Referees, Analysts, Trainers',
      description: 'Join our network of sports professionals and connect with players and clubs looking for your expertise.',
      benefits: [
        'Create a professional profile showcasing your credentials',
        'Receive direct booking requests from players and clubs',
        'Set your own rates and availability',
        'Build your reputation with reviews and ratings',
        'Access to a growing community of sports enthusiasts',
        'Priority listing in search results',
      ],
      stats: { partners: '200+', bookings: '5000+', rating: '4.8' },
      color: 'from-blue-500/20 to-blue-600/10',
    },
    {
      id: 'clubs',
      icon: Trophy,
      title: 'Sports Clubs',
      subtitle: 'Amateur to Professional Clubs',
      description: 'Register your club on Playmatez and unlock new opportunities for growth, competition, and community building.',
      benefits: [
        'Manage your club members and teams efficiently',
        'Find and recruit new players easily',
        'Challenge other clubs for matches and tournaments',
        'Book venues at preferential rates',
        'Organize and promote events to a wider audience',
        'Access analytics and performance tracking',
      ],
      stats: { partners: '150+', members: '3000+', matches: '500+' },
      color: 'from-green-500/20 to-green-600/10',
    },
    {
      id: 'venues',
      icon: MapPin,
      title: 'Sports Venues',
      subtitle: 'Turfs, Courts, Stadiums, Arenas',
      description: 'List your sports venue on Playmatez and reach thousands of players and clubs looking for the perfect place to play.',
      benefits: [
        'Get discovered by 10,000+ active users',
        'Manage all bookings through a single dashboard',
        'Reduce no-shows with automated reminders',
        'Accept secure online payments',
        'Showcase facilities with photos and videos',
        'Get featured in our venue recommendations',
      ],
      stats: { partners: '100+', bookings: '10000+', revenue: '₹50L+' },
      color: 'from-purple-500/20 to-purple-600/10',
    },
  ];

  const whyPartner = [
    {
      icon: Globe,
      title: 'Massive Reach',
      description: 'Access our growing community of 10,000+ sports enthusiasts across India.',
    },
    {
      icon: TrendingUp,
      title: 'Growth Opportunity',
      description: 'Expand your business with new customers discovering you through our platform.',
    },
    {
      icon: Shield,
      title: 'Trusted Platform',
      description: 'Benefit from our verified reviews, secure payments, and trusted brand.',
    },
    {
      icon: Zap,
      title: 'Easy Integration',
      description: 'Simple onboarding process with dedicated support to get you started.',
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-primary/5 via-transparent to-transparent" />
        <div className="container-custom relative z-10">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-primary mb-4">
              Partnership Program
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-white mb-6">
              Grow With <span className="gradient-text">Playmatez</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
              Join India's fastest-growing sports networking platform. Partner with us as a 
              Sports Professional, Club, or Venue and unlock new opportunities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Why Partner Section */}
      <section className="py-16 bg-card/30">
        <div className="container-custom">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Why Partner With Us?
            </h2>
          </motion.div>
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {whyPartner.map((item) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  variants={itemVariants}
                  className="text-center p-6"
                >
                  <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-primary" />
                  </div>
                  <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-muted-foreground text-sm">{item.description}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Partner Types */}
      <section className="py-20">
        <div className="container-custom">
          <motion.div
            className="text-center mb-12"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">
              Choose Your Partnership Type
            </h2>
            <p className="text-muted-foreground max-w-xl mx-auto">
              Select the category that best describes you to learn more about partnership benefits.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {partnerTypes.map((type) => {
              const Icon = type.icon;
              const isSelected = selectedPartnerType === type.id;
              
              return (
                <motion.div
                  key={type.id}
                  variants={itemVariants}
                  className={`relative rounded-2xl border transition-all duration-300 cursor-pointer overflow-hidden ${
                    isSelected 
                      ? 'border-primary bg-gradient-to-b ' + type.color 
                      : 'border-white/10 bg-card hover:border-white/20'
                  }`}
                  onClick={() => setSelectedPartnerType(isSelected ? null : type.id)}
                  data-testid={`partner-type-${type.id}`}
                >
                  <div className="p-8">
                    {/* Header */}
                    <div className="flex items-start justify-between mb-6">
                      <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center">
                        <Icon className="w-7 h-7 text-primary" />
                      </div>
                      {isSelected && (
                        <CheckCircle className="w-6 h-6 text-primary" />
                      )}
                    </div>

                    {/* Content */}
                    <h3 className="font-heading text-xl font-bold text-white mb-1">
                      {type.title}
                    </h3>
                    <p className="text-primary text-sm mb-4">{type.subtitle}</p>
                    <p className="text-muted-foreground text-sm mb-6">
                      {type.description}
                    </p>

                    {/* Stats */}
                    <div className="flex items-center gap-4 mb-6 pb-6 border-b border-white/10">
                      {Object.entries(type.stats).map(([key, value]) => (
                        <div key={key} className="text-center">
                          <p className="font-heading font-bold text-white">{value}</p>
                          <p className="text-xs text-muted-foreground capitalize">{key}</p>
                        </div>
                      ))}
                    </div>

                    {/* Benefits */}
                    <div className={`space-y-3 overflow-hidden transition-all duration-500 ${
                      isSelected ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                    }`}>
                      <p className="text-sm font-semibold text-white mb-3">Benefits:</p>
                      {type.benefits.map((benefit, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-muted-foreground">{benefit}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <button
                      className={`w-full mt-6 px-6 py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 ${
                        isSelected 
                          ? 'bg-primary text-white hover:bg-primary/90' 
                          : 'bg-white/5 text-white hover:bg-white/10'
                      }`}
                    >
                      {isSelected ? 'Apply Now' : 'Learn More'}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Application Form CTA */}
      <section className="py-24 relative overflow-hidden bg-card/30">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/10 via-transparent to-secondary/10" />
        <div className="container-custom relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Users className="w-8 h-8 text-primary" />
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              Ready to Partner?
            </h2>
            <p className="text-muted-foreground mb-8 max-w-lg mx-auto">
              Fill out our partnership application form and our team will get back to you 
              within 48 hours to discuss the next steps.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <motion.a
                href="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-white font-semibold rounded-full hover:bg-primary/90 transition-colors"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                data-testid="partner-apply-btn"
              >
                Apply for Partnership
                <ArrowRight className="w-5 h-5" />
              </motion.a>
              <a
                href="mailto:connect@playmatez.io"
                className="text-muted-foreground hover:text-white transition-colors"
              >
                or email us at connect@playmatez.io
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PartnerPage;
